import { useMemo, useCallback } from "react";
import { scaleBand, scaleLinear } from "@visx/scale";
import { AxisBottom, AxisLeft } from "@visx/axis";
import { GridRows } from "@visx/grid";
import { Group } from "@visx/group";
import { ParentSize } from "@visx/responsive";
import { useTooltip } from "@visx/tooltip";
import { ChartTooltip } from "../../../shared/components/ChartTooltip/ChartTooltip";
import type { ThreadDepthResponse } from "../../types";
import { formatNumber } from "../../utils/formatters";
import styles from "./ThreadDepthChart.module.css";

type Bucket = ThreadDepthResponse["distribution"][number];

const MARGIN = { top: 16, right: 16, bottom: 40, left: 48 };
const BAR_COLOR = "#6366f1";
const BAR_HOVER_COLOR = "#818cf8";

interface InnerChartProps {
  buckets: Bucket[];
  width: number;
  height: number;
}

function InnerChart({ buckets, width, height }: InnerChartProps) {
  const {
    tooltipOpen,
    tooltipData,
    tooltipLeft,
    tooltipTop,
    showTooltip,
    hideTooltip,
  } = useTooltip<Bucket>();

  const innerWidth = width - MARGIN.left - MARGIN.right;
  const innerHeight = height - MARGIN.top - MARGIN.bottom;

  const xScale = useMemo(
    () =>
      scaleBand<string>({
        domain: buckets.map((b) => b.label),
        range: [0, innerWidth],
        padding: 0.3,
      }),
    [buckets, innerWidth],
  );

  const yScale = useMemo(
    () =>
      scaleLinear<number>({
        domain: [0, buckets.reduce((m, b) => Math.max(m, b.count), 0) * 1.15 || 1],
        range: [innerHeight, 0],
        nice: true,
      }),
    [buckets, innerHeight],
  );

  const handleMouseOver = useCallback(
    (bucket: Bucket) => {
      const barX = xScale(bucket.label) ?? 0;
      const barWidth = xScale.bandwidth();
      showTooltip({
        tooltipData: bucket,
        tooltipLeft: MARGIN.left + barX + barWidth / 2,
        tooltipTop: MARGIN.top + yScale(bucket.count),
      });
    },
    [xScale, yScale, showTooltip],
  );

  if (innerWidth <= 0 || innerHeight <= 0) return null;

  return (
    <div className={styles.chartWrapper}>
      <svg width={width} height={height} role="img" aria-label="Thread depth distribution">
        <Group left={MARGIN.left} top={MARGIN.top}>
          <GridRows
            scale={yScale}
            width={innerWidth}
            stroke="var(--colours-neutral-100, #f3f4f6)"
          />
          {buckets.map((bucket) => {
            const barWidth = xScale.bandwidth();
            const barHeight = innerHeight - yScale(bucket.count);
            const barX = xScale(bucket.label) ?? 0;
            const barY = yScale(bucket.count);
            const isHovered = tooltipData?.label === bucket.label;
            return (
              <rect
                key={bucket.label}
                x={barX}
                y={barY}
                width={barWidth}
                height={Math.max(barHeight, 0)}
                fill={isHovered ? BAR_HOVER_COLOR : BAR_COLOR}
                rx={3}
                onMouseMove={() => handleMouseOver(bucket)}
                onMouseLeave={hideTooltip}
              />
            );
          })}
          <AxisBottom
            top={innerHeight}
            scale={xScale}
            stroke="var(--colours-neutral-200, #e5e7eb)"
            tickStroke="var(--colours-neutral-200, #e5e7eb)"
            tickLabelProps={() => ({
              fill: "var(--colours-neutral-500, #6b7280)",
              fontSize: 11,
              textAnchor: "middle" as const,
            })}
            label="Messages per thread"
            labelOffset={20}
            labelProps={{
              fill: "var(--colours-neutral-500, #6b7280)",
              fontSize: 11,
              textAnchor: "middle" as const,
            }}
          />
          <AxisLeft
            scale={yScale}
            numTicks={5}
            stroke="var(--colours-neutral-200, #e5e7eb)"
            tickStroke="var(--colours-neutral-200, #e5e7eb)"
            tickLabelProps={() => ({
              fill: "var(--colours-neutral-500, #6b7280)",
              fontSize: 11,
              textAnchor: "end" as const,
              dx: -4,
              dy: 4,
            })}
            label="Threads"
            labelOffset={32}
            labelProps={{
              fill: "var(--colours-neutral-500, #6b7280)",
              fontSize: 11,
              textAnchor: "middle" as const,
            }}
          />
        </Group>
      </svg>
      {tooltipOpen && tooltipData && (
        <ChartTooltip top={tooltipTop ?? 0} left={tooltipLeft ?? 0}>
          <div className={styles.tooltipContent}>
            <span className={styles.tooltipLabel}>{tooltipData.label} messages</span>
            <span>
              <strong>{formatNumber(tooltipData.count)}</strong> threads
            </span>
          </div>
        </ChartTooltip>
      )}
    </div>
  );
}

export interface ThreadDepthChartProps {
  data: ThreadDepthResponse;
}

const CHART_HEIGHT = 280;

export function ThreadDepthChart({ data }: ThreadDepthChartProps) {
  if (data.distribution.length === 0) {
    return (
      <div className={styles.emptyState}>No thread depth data for this period.</div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.stats}>
        <div className={styles.stat}>
          <span className={styles.statLabel}>Total</span>
          <span className={styles.statValue}>{formatNumber(data.total_threads)}</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statLabel}>Avg Depth</span>
          <span className={styles.statValue}>{data.average_depth.toFixed(1)}</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statLabel}>Max Depth</span>
          <span className={styles.statValue}>{formatNumber(data.max_depth)}</span>
        </div>
      </div>
      <ParentSize>
        {({ width }) => (
          <InnerChart
            buckets={data.distribution}
            width={width}
            height={CHART_HEIGHT}
          />
        )}
      </ParentSize>
    </div>
  );
}
